import { getApiClient, NO_BACKOFF, type File } from '@littlebird/sdk';
import { type QueryClient } from '@tanstack/react-query';

import {
  artifactPolicy,
  getFileVersionKey,
} from '@/features/artifacts/artifact-presentation';
import { PAPER_PAGE_WIDTH_PX } from '@/features/artifacts/artifact.constants';
import { artifactBodyQueryOptions } from '@/features/artifacts/artifact.query';
import { renderCanvasThumbnail } from '@/features/canvas/canvas-export';
import {
  readCanvasHostThemeForMode,
  type CanvasHostTheme,
} from '@/features/canvas/canvas-theme';
import { logError } from '@/lib/errorLogging';
import { type Theme } from '@/stores/ui.store';
import { inElectron } from '@/utils/capabilities';

import { spritesStore } from './sprites.store';

const THUMBNAIL_MAX_HEIGHT = Math.round((PAPER_PAGE_WIDTH_PX * 3) / 4);

interface ThumbnailRequest {
  file: File;
  queryClient: QueryClient;
  getFileUrl: (file: File) => Promise<string | undefined>;
}

interface ThumbnailJob {
  request: ThumbnailRequest;
  versionKey: string;
  attempt: number;
}

const MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1_000;
const MAX_CLAIMED_VERSIONS = 500;
const MAX_QUEUED_JOBS = 12;

const claimed = new Map<string, true>();
const queue: ThumbnailJob[] = [];
let processing = false;

const hostThemes = new Map<Theme, CanvasHostTheme>();
const hostThemeFor = (mode: Theme): CanvasHostTheme => {
  const cached = hostThemes.get(mode);
  if (cached) return cached;
  const hostTheme = readCanvasHostThemeForMode(mode);
  hostThemes.set(mode, hostTheme);
  return hostTheme;
};

const toWebpBlob = (bytes: Uint8Array) =>
  new Blob([new Uint8Array(bytes)], { type: 'image/webp' });

const renderVariant = async (
  canvasText: string,
  mode: Theme
): Promise<Uint8Array> => {
  const result = await renderCanvasThumbnail({
    canvasText,
    hostTheme: hostThemeFor(mode),
    maxHeight: THUMBNAIL_MAX_HEIGHT,
    layoutWidth: PAPER_PAGE_WIDTH_PX,
  });
  if (!result.ok) throw new Error(result.error);
  return result.bytes;
};

const generate = async ({
  file,
  queryClient,
  getFileUrl,
}: ThumbnailRequest) => {
  const body = await queryClient.fetchQuery(
    artifactBodyQueryOptions(
      queryClient,
      getFileVersionKey(file),
      () => getFileUrl(file),
      'canvas',
      artifactPolicy(file).byteCap
    )
  );
  if (body.outcome !== 'ready') return;

  const light = await renderVariant(body.text, 'light');
  const dark = await renderVariant(body.text, 'dark');

  const form = new FormData();
  form.append('light', toWebpBlob(light), 'light.webp');
  form.append('dark', toWebpBlob(dark), 'dark.webp');
  const { thumbnail_url: thumbnailUrl } = await getApiClient().fetch<{
    thumbnail_url: string;
  }>(`sprites/thumbnail/${file.id}`, {
    method: 'PUT',
    body: form,
    backoff: NO_BACKOFF,
  });

  spritesStore.applyThumbnail(file.id, thumbnailUrl);
};

const processQueue = async () => {
  if (processing) return;
  processing = true;
  while (queue.length > 0) {
    const job = queue.shift();
    if (!job) continue;
    try {
      await generate(job.request);
    } catch (error: unknown) {
      logError(error, 'Failed to generate an artifact thumbnail', {
        context: 'requestSpriteThumbnail',
        metadata: { fileId: job.request.file.id },
      });
      if (job.attempt < MAX_ATTEMPTS) {
        const enqueueRetry = () => {
          if (!enqueueAttempt(job.request, job.versionKey, job.attempt + 1)) {
            setTimeout(enqueueRetry, RETRY_BASE_DELAY_MS);
          }
        };
        setTimeout(enqueueRetry, RETRY_BASE_DELAY_MS * 2 ** (job.attempt - 1));
      } else {
        claimed.delete(job.versionKey);
      }
    }
  }
  processing = false;
};

const enqueueAttempt = (
  request: ThumbnailRequest,
  versionKey: string,
  attempt: number
): boolean => {
  if (queue.length >= MAX_QUEUED_JOBS) return false;
  queue.push({ request, versionKey, attempt });
  void processQueue();
  return true;
};

const claimVersion = (versionKey: string): boolean => {
  if (claimed.has(versionKey)) {
    claimed.delete(versionKey);
    claimed.set(versionKey, true);
    return false;
  }
  claimed.set(versionKey, true);
  if (claimed.size > MAX_CLAIMED_VERSIONS) {
    const oldestVersionKey = claimed.keys().next().value;
    if (oldestVersionKey) claimed.delete(oldestVersionKey);
  }
  return true;
};

export const requestSpriteThumbnail = (request: ThumbnailRequest): boolean => {
  if (!inElectron()) return true;
  const versionKey = getFileVersionKey(request.file);
  if (!claimVersion(versionKey)) return true;
  if (enqueueAttempt(request, versionKey, 1)) return true;
  claimed.delete(versionKey);
  return false;
};
